/**
 * SequencingLiveDashboardPage — iter150AG Phase 1E.
 *
 * Single-screen live operations view per the design doc §20:
 *   • Current pressure with band overlay (the "where in the band are we"
 *     visual).
 *   • Pset + bandwidth + delay timers.
 *   • Running / stopped compressor grid with lead/lag labels and
 *     working hours.
 *   • Active faults panel.
 *   • Schedule status chip (no_stop_window | stop_window_active |
 *     holiday_no_run).
 *   • Next expected action — the most recent decision rendered as a
 *     human-readable sentence with a delay countdown when applicable.
 *
 * Polls every 5 seconds. CP-mode badge in the header so operators know
 * whether they're watching the real fleet or the mock.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Gauge, Thermometer, Lightning, Pause, Warning, Clock,
  CheckCircle, CircleNotch, ChartLine, MapPin, Calendar, FlowArrow,
} from "@phosphor-icons/react";
import { seqGroupDashboard, seqGroupAlerts, seqMemberStartStop } from "@/lib/sequencingApi";
import TelemetryDiagnosticsPanel from "@/components/sequencing/TelemetryDiagnosticsPanel";
import LearnMoreChip from "@/components/help/LearnMoreChip";


// ════════════════════════════════════════════════════════════════════
// iter150AG Part 19 — shared StatusDot component.
//
//   • blue blinking  → compressor running
//   • red blinking   → engine is holding off (restart_delay / stop_delay)
//   • gray static    → stopped, no engine action pending
//   • slate static   → no telemetry yet (unknown)
//   • emerald static → engine state OK / "live" badge replacement
//   • yellow pulsing → "connection alive" heartbeat
//
// The animation uses inline CSS keyframes so a single file change ships
// the visual without touching the global stylesheet.
// ════════════════════════════════════════════════════════════════════
const STATUS_DOT_KEYFRAMES = `
@keyframes seq-dot-blink {
  0%, 100% { opacity: 1;   transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.78); }
}
@keyframes seq-dot-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0  currentColor; }
  50%      { opacity: 0.55; box-shadow: 0 0 0 5px transparent; }
}
`;

function StatusDot({ tone = "slate", animate = "none", size = 10, title }) {
  const colors = {
    blue:    "bg-blue-500   text-blue-300",
    red:     "bg-rose-500   text-rose-300",
    gray:    "bg-slate-300  text-slate-200",
    slate:   "bg-slate-400  text-slate-300",
    green:   "bg-emerald-500 text-emerald-300",
    amber:   "bg-amber-400  text-amber-300",
    yellow:  "bg-yellow-400 text-yellow-200",
  };
  const animStyle = animate === "blink"
    ? { animation: "seq-dot-blink 1.1s ease-in-out infinite" }
    : animate === "pulse"
      ? { animation: "seq-dot-pulse 1.4s ease-in-out infinite" }
      : undefined;
  return (
    <span title={title}
          data-testid={`status-dot-${tone}${animate !== "none" ? `-${animate}` : ""}`}
          className={`inline-block rounded-full ${colors[tone] || colors.slate}`}
          style={{ width: size, height: size, ...(animStyle || {}) }} />
  );
}

function memberDot(m) {
  // Decide which dot to render based on run state + engine_hold.
  // Returns { tone, animate, title }.
  if (m?.engine_hold === "restart_delay") {
    return { tone: "red", animate: "blink",
              title: "Stopped — engine is holding off (restart delay). Click 'Tick now' or wait for the delay to elapse." };
  }
  if (m?.engine_hold === "stop_delay") {
    return { tone: "red", animate: "blink",
              title: "Running, but engine will stop shortly (stop delay)." };
  }
  if (m?.running === true) {
    return { tone: "blue", animate: "blink", title: "Compressor is running" };
  }
  if (m?.running === false) {
    return { tone: "gray", animate: "none", title: "Compressor is stopped" };
  }
  return { tone: "slate", animate: "none", title: "No telemetry yet" };
}


const POLL_MS = 5000;


export default function SequencingLiveDashboardPage() {
  const { gid } = useParams();
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState({ open: [], closed: [], open_count: 0 });
  const [err,  setErr]  = useState("");
  const [diagOpen, setDiagOpen] = useState(false);
  const timerRef = useRef(null);

  async function fetchOnce() {
    try {
      const [d, a] = await Promise.all([
        seqGroupDashboard(gid),
        seqGroupAlerts(gid).catch(() => ({ open: [], closed: [], open_count: 0 })),
      ]);
      setDash(d);
      setAlerts(a || { open: [], closed: [], open_count: 0 });
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    }
  }
  useEffect(() => {
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid]);

  if (err && !dash) return (
    <div className="p-6 text-red-700 text-sm" data-testid="dash-error">{err}</div>
  );
  if (!dash) return (
    <div className="p-6 text-slate-500 text-sm flex items-center gap-2" data-testid="dash-loading">
      <CircleNotch size={14} className="animate-spin" /> Loading dashboard…
    </div>
  );

  const stateColor = dash.group.state === "auto_sequencing" ? "emerald"
                    : dash.group.state === "paused"          ? "amber"
                    : dash.group.state === "fault"           ? "rose"
                                                              : "slate";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sequencing-live-dashboard">
      {/* iter150AG Part 19 — one-shot inline keyframes for the status dots. */}
      <style>{STATUS_DOT_KEYFRAMES}</style>
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to={`/sequencing/groups/${gid}`} data-testid="dash-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <ChartLine size={22} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate inline-flex items-center gap-2">
            {/* iter150AG Part 19 — engine-state dot replaces the "— live"
                suffix. Green = AUTO_SEQUENCING, amber = COMMISSIONING/HOLD,
                slate otherwise. */}
            <StatusDot
              tone={
                dash.group.state === "AUTO_SEQUENCING" ? "green"
                : (dash.group.state || "").includes("HOLD") ? "amber"
                : "slate"}
              size={11}
              title={`Engine state: ${dash.group.state}`}
              animate="none" />
            {dash.group.name}
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            {dash.group.customer_name
              ? <span className="font-bold text-slate-700">{dash.group.customer_name}</span>
              : <span className="italic text-slate-400">unlinked tenant</span>}
            {dash.fetched_at && (
              <> · refreshed {new Date(dash.fetched_at).toLocaleTimeString()}</>
            )}
          </p>
        </div>
        {/* iter150AG Part 19 — Connection-alive pulse, now clickable
            (Part 20) to open the telemetry diagnostics deep-dive. */}
        <button type="button" onClick={() => setDiagOpen(true)}
                data-testid="dash-conn-pulse-btn"
                title="Open telemetry diagnostics"
                className="inline-flex items-center gap-1.5 hover:bg-slate-50 px-1.5 py-1 rounded-sm transition-colors">
          <StatusDot
            tone={
              dash.fetched_at && (Date.now() - new Date(dash.fetched_at).getTime()) < 15000
                ? "yellow" : "amber"}
            animate={
              dash.fetched_at && (Date.now() - new Date(dash.fetched_at).getTime()) < 15000
                ? "pulse" : "none"}
            size={10}
            title={dash.fetched_at
              ? `Last update: ${new Date(dash.fetched_at).toLocaleTimeString()}`
              : "No update yet"} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden md:inline">
            {dash.fetched_at && (Date.now() - new Date(dash.fetched_at).getTime()) < 15000
              ? "Connected" : "Stale"}
          </span>
        </button>
        <span
          data-testid={`dash-state-${dash.group.state}`}
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-${stateColor}-50 text-${stateColor}-700 border-${stateColor}-300`}>
          {dash.group.state}
        </span>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-5 grid grid-cols-12 gap-4">
        {/* Pressure tile — big (with active setpoint readback inside) */}
        <PressureCard pressure={dash.pressure} pset={dash.pset} />

        {/* Running / stopped counters */}
        <CountersCard members={dash.members}
                       running={dash.running_count}
                       stopped={dash.stopped_count}
                       unknown={dash.unknown_count}
                       faults={dash.faults?.count || 0} />

        {/* Schedule status */}
        <ScheduleCard schedule={dash.schedule} />

        {/* Next expected action */}
        <NextActionCard next_action={dash.next_action}
                         engine_enabled={dash.group.engine_enabled}
                         state={dash.group.state} />

        {/* Members grid */}
        <MembersCard members={dash.members} groupId={gid} onChanged={fetchOnce} />

        {/* iter150AG Part 9 (P2) — Fault alert banner */}
        <AlertBanner alerts={alerts} />

        {/* Faults */}
        <FaultsCard faults={dash.faults} />

        {/* Recent decisions ticker */}
        <RecentDecisionsCard items={dash.recent_decisions} />
      </main>
      {diagOpen && (
        <TelemetryDiagnosticsPanel gid={gid} onClose={() => setDiagOpen(false)} />
      )}
    </div>
  );
}


function PressureCard({ pressure, pset }) {
  const cur  = pressure?.current;
  const low  = pressure?.low_band;
  const high = pressure?.high_band;
  const samples = pressure?.samples || [];
  let bandStatus = "—";
  let bandColor  = "slate";
  if (cur != null && low != null && high != null) {
    if (cur < low)       { bandStatus = "below band"; bandColor = "rose"; }
    else if (cur > high) { bandStatus = "above band"; bandColor = "rose"; }
    else                 { bandStatus = "in band";    bandColor = "emerald"; }
  }
  // Position cursor on the band bar (clamp to [0,100]).
  let percent = 50;
  if (cur != null && low != null && high != null) {
    const span = (high - low) * 3;        // 3x band width window
    const lo   = low - span / 3;
    percent = Math.max(0, Math.min(100, ((cur - lo) / span) * 100));
  }
  return (
    <section data-testid="dash-pressure-card"
             className="col-span-12 md:col-span-8 bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Gauge size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Current pressure</h2>
        <span data-testid={`dash-band-${bandColor}`}
              className={`ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm
                          bg-${bandColor}-100 text-${bandColor}-700 border border-${bandColor}-300`}>
          {bandStatus}
        </span>
      </div>
      <div className="flex items-baseline gap-3">
        <div className="text-5xl md:text-6xl font-bold font-['Chivo'] text-slate-900 tabular-nums tracking-tight"
             data-testid="dash-pressure-value">
          {cur != null ? cur.toFixed(2) : "—"}
        </div>
        <div className="text-sm text-slate-500 font-mono">bar</div>
        {cur == null && (
          /* iter150AG Part 24 — make "no pressure data" actionable
              instead of silently showing "—". Surfaces a button
              straight to the diagnostics deep-dive. */
          <div data-testid="dash-pressure-empty-hint"
               className="ml-2 text-[10px] leading-tight bg-amber-50 border border-amber-300 rounded-sm p-1.5 max-w-[260px]">
            <div className="font-bold text-amber-900 uppercase tracking-wider mb-0.5 flex items-center gap-1.5">
              No pressure data
              <span className="ml-auto">
                <LearnMoreChip slug="06-cp-webhook-secret" label="Help" />
              </span>
            </div>
            <div className="text-amber-800">
              CP isn't publishing a recognised <code>Pressure</code> point for this group.
              Open <b>Diagnostics</b> (click the heartbeat dot) → <b>Capture next 20</b> to inspect what's actually being delivered.
            </div>
          </div>
        )}
        {/* iter150AG — inline live sparkline (last ~60 samples per
            compressor — all members plotted on the same chart). */}
        <PressureSparkline samples={samples}
                            memberSamples={pressure?.member_samples}
                            low={low} high={high} />
        <div className="ml-auto text-[11px] text-slate-500 text-right">
          <div>Pset <b className="font-mono text-slate-700">{pset?.common_pset ?? "—"}</b> bar</div>
          <div>Band <b className="font-mono text-slate-700">
            {low != null ? low.toFixed(2) : "—"}–{high != null ? high.toFixed(2) : "—"}
          </b> bar</div>
          {/* iter150AG Part 13 — Active VSD pressure setpoint readback.
              Shows the value the compressor is currently holding (via
              the readable ``pressure_setpoint`` alias). A "≠ target"
              warning surfaces when the active value drifts from the
              configured group Pset — usually a mis-configured alias
              or a manual override on the panel. */}
          {pressure?.active_setpoint_bar != null && (
            <div data-testid="dash-active-setpoint" className="mt-1 pt-1 border-t border-slate-200">
              Active setpoint <b className="font-mono text-emerald-700">
                {Number(pressure.active_setpoint_bar).toFixed(2)}
              </b> bar
              {pressure?.common_pset != null &&
                Math.abs(pressure.active_setpoint_bar - pressure.common_pset) > 0.05 && (
                <span data-testid="dash-setpoint-drift"
                       className="ml-1 inline-block bg-amber-50 border border-amber-300 text-amber-800 px-1 rounded-sm font-bold"
                       title="Active setpoint on the compressor differs from the configured group Pset.">
                  ≠ target
                </span>
              )}
            </div>
          )}
          {pressure?.active_setpoint_bar == null && pressure?.common_pset != null && (
            <div className="mt-1 pt-1 border-t border-slate-200 text-slate-400 italic">
              Active setpoint: not configured
            </div>
          )}
        </div>
      </div>
      {/* Band bar */}
      {(low != null && high != null) && (
        <div className="relative h-3 rounded-full bg-slate-100 mt-2" data-testid="dash-band-bar">
          {/* Band shading */}
          <div className="absolute inset-y-0 left-1/3 right-1/3 bg-emerald-200/70 rounded-full" />
          <div className="absolute inset-y-0 left-1/3 w-px bg-emerald-600" />
          <div className="absolute inset-y-0 right-1/3 w-px bg-emerald-600" />
          {/* Pressure cursor */}
          <div data-testid="dash-band-cursor"
               className="absolute -top-1 h-5 w-1 bg-slate-900 rounded-sm transition-all duration-500"
               style={{ left: `calc(${percent}% - 2px)` }} />
        </div>
      )}
      <div className="flex justify-between text-[10px] text-slate-400 font-mono">
        <span>{low != null ? (low - (high - low)).toFixed(1) : "—"}</span>
        <span>{low?.toFixed?.(2)}</span>
        <span>{high?.toFixed?.(2)}</span>
        <span>{high != null ? (high + (high - low)).toFixed(1) : "—"}</span>
      </div>
    </section>
  );
}


/**
 * PressureSparkline — inline SVG line chart of the last N pressure
 * samples for every compressor in the group. The in-band region is
 * shaded green and each compressor gets its own color so the operator
 * can see at-a-glance whether the lead's pressure correlates with
 * lag activity.
 *
 * Falls back to single-line mode if ``memberSamples`` is empty (older
 * /dashboard payloads).
 *
 * Design notes:
 *   - All lines share one y-scale so values are directly comparable.
 *   - The line for the primary device is bold + dark; lag devices use
 *     muted hues from a stable palette indexed by sort_order so the
 *     colour for "Lag 1" never re-shuffles between renders.
 *   - A compact legend below the chart maps each colour to its
 *     Lead/Lag label + serial.
 */
const _SERIES_COLORS = [
  "rgb(15, 23, 42)",       // slate-900 — Lead
  "rgb(37, 99, 235)",      // blue-600  — Lag 1
  "rgb(217, 119, 6)",      // amber-600 — Lag 2
  "rgb(5, 150, 105)",      // emerald-600
  "rgb(190, 24, 93)",      // pink-700
  "rgb(124, 58, 237)",     // violet-600
  "rgb(13, 148, 136)",     // teal-600
  "rgb(202, 138, 4)",      // yellow-600
];
function _seriesColor(idx) {
  return _SERIES_COLORS[idx % _SERIES_COLORS.length];
}

function PressureSparkline({ samples, memberSamples, low, high, width = 200, height = 56 }) {
  // Normalise into an array of {label, samples[], isPrimary, color}.
  let series = [];
  if (Array.isArray(memberSamples) && memberSamples.length > 0) {
    series = memberSamples
      .filter(s => Array.isArray(s.samples) && s.samples.length >= 2)
      .map((s, i) => ({
        key:        s.device_id || `s${i}`,
        label:      s.lead_lag_label || `Member ${i + 1}`,
        deviceId:   s.device_id,
        isPrimary:  !!s.is_primary,
        samples:    s.samples,
        color:      _seriesColor((s.sort_order || (i + 1)) - 1),
      }));
  }
  if (series.length === 0 && Array.isArray(samples) && samples.length >= 2) {
    series = [{
      key: "primary", label: "Lead", isPrimary: true,
      samples, color: _seriesColor(0),
    }];
  }
  if (series.length === 0) {
    return (
      <div data-testid="dash-sparkline-empty"
           className="ml-3 text-[10px] text-slate-400 self-center" style={{ width, height }}>
        <div className="h-full flex items-center justify-center border border-dashed border-slate-200 rounded-sm">
          collecting samples…
        </div>
      </div>
    );
  }

  // Shared y-range across all series + the band.
  let yMin = Infinity, yMax = -Infinity;
  for (const s of series) {
    for (const p of s.samples) {
      if (p.value < yMin) yMin = p.value;
      if (p.value > yMax) yMax = p.value;
    }
  }
  if (low  != null) yMin = Math.min(yMin, low);
  if (high != null) yMax = Math.max(yMax, high);
  if (!isFinite(yMin) || !isFinite(yMax)) { yMin = 0; yMax = 1; }
  const yPad = Math.max(0.05, (yMax - yMin) * 0.15);
  const lo = yMin - yPad, hi = yMax + yPad;
  const padX = 4, padY = 4;
  const w = width - padX * 2;
  const h = height - padY * 2;
  const yFor = v => padY + (1 - (v - lo) / Math.max(0.001, hi - lo)) * h;
  const bandTop    = low != null && high != null ? yFor(high) : null;
  const bandBottom = low != null && high != null ? yFor(low)  : null;

  // Each line uses its own x-scale (samples may have different counts).
  function pathFor(pts) {
    const n = pts.length;
    const xFor = i => padX + (i / Math.max(1, n - 1)) * w;
    return pts.map((p, i) => `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)},${yFor(p.value).toFixed(1)}`).join(" ");
  }

  return (
    <div className="ml-3 shrink-0 self-center" data-testid="dash-sparkline-wrap">
      <svg
        data-testid="dash-sparkline"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Pressure trend for ${series.length} compressor(s)`}
        className="block">
        {bandTop != null && bandBottom != null && (
          <rect x={padX} y={bandTop}
                width={w}
                height={Math.max(1, bandBottom - bandTop)}
                fill="rgb(167, 243, 208)" fillOpacity={0.35}
                data-testid="dash-sparkline-band" />
        )}
        {series.map(s => {
          const last = s.samples[s.samples.length - 1];
          const inBand = (low == null || last.value >= low) && (high == null || last.value <= high);
          const lastX = padX + w;
          const lastY = yFor(last.value);
          return (
            <g key={s.key} data-testid={`dash-sparkline-line-${s.deviceId || s.key}`}>
              <path d={pathFor(s.samples)} fill="none"
                    stroke={s.color}
                    strokeWidth={s.isPrimary ? 1.8 : 1.2}
                    strokeOpacity={s.isPrimary ? 1 : 0.85}
                    strokeLinejoin="round" strokeLinecap="round" />
              <circle cx={lastX} cy={lastY}
                      r={s.isPrimary ? 2.6 : 2.0}
                      fill={inBand ? s.color : "rgb(225, 29, 72)"}
                      stroke="white" strokeWidth={0.6} />
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <ul data-testid="dash-sparkline-legend"
            className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] leading-tight"
            style={{ width }}>
          {series.map(s => (
            <li key={s.key}
                data-testid={`dash-sparkline-legend-${s.deviceId || s.key}`}
                className={`inline-flex items-center gap-1 ${s.isPrimary ? "font-bold text-slate-700" : "text-slate-500"}`}
                title={s.deviceId}>
              <span className="inline-block rounded-full"
                    style={{ width: 6, height: 6, background: s.color }} />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function CountersCard({ members, running, stopped, unknown = 0, faults }) {
  return (
    <section data-testid="dash-counters-card"
             className="col-span-12 md:col-span-4 bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Lightning size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Status</h2>
        {unknown > 0 && (
          <span data-testid="dash-unknown-count-chip"
                title={`${unknown} member(s) have not reported telemetry yet`}
                className="ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-300">
            {unknown} unknown
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2"
             data-testid="dash-running-count">
          <div className="text-2xl font-bold text-emerald-700 tabular-nums">{running}</div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Running</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-2"
             data-testid="dash-stopped-count">
          <div className="text-2xl font-bold text-slate-700 tabular-nums">{stopped}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-600">Stopped</div>
        </div>
        <div className={`rounded-sm p-2 border ${
          faults > 0
            ? "bg-rose-50 border-rose-300"
            : "bg-slate-50 border-slate-200"}`}
             data-testid="dash-fault-count">
          <div className={`text-2xl font-bold tabular-nums ${
            faults > 0 ? "text-rose-700" : "text-slate-400"}`}>
            {faults}
          </div>
          <div className={`text-[10px] uppercase tracking-wider ${
            faults > 0 ? "text-rose-700" : "text-slate-500"}`}>Faults</div>
        </div>
      </div>
      <div className="text-[10px] text-slate-400 text-center">
        Total members: <b className="text-slate-700">{members?.length || 0}</b>
      </div>
    </section>
  );
}


function ScheduleCard({ schedule }) {
  const label = schedule?.label || "—";
  // iter150AG Part 15 — STOP-window semantics.
  //   no_stop_window      → engine free to run (green).
  //   stop_window_active  → engine sheds (rose).
  //   holiday_no_run      → tenant holiday, no run (amber).
  const color = label === "no_stop_window"     ? "emerald"
              : label === "stop_window_active" ? "rose"
              : label === "holiday_no_run"     ? "amber"
                                               : "slate";
  const friendly = label === "no_stop_window"     ? "running"
                 : label === "stop_window_active" ? "in stop window"
                 : label === "holiday_no_run"     ? "holiday – no run"
                                                  : label.replace(/_/g, " ");
  return (
    <section data-testid="dash-schedule-card"
             className="col-span-6 md:col-span-4 bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2">
        <Calendar size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Schedule</h2>
        <span data-testid={`dash-schedule-${label}`}
              className={`ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm
                          bg-${color}-100 text-${color}-700 border border-${color}-300`}>
          {friendly}
        </span>
      </div>
      <div className="mt-2 text-[11px] text-slate-600 space-y-0.5">
        <div>Today: <b>{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][schedule?.today_weekday] || "—"}</b></div>
        {schedule?.todays_slot && (
          <div>Stop window: <span className="font-mono">
            {schedule.todays_slot.start_time || "—"} → {schedule.todays_slot.end_time || "—"}
          </span></div>
        )}
        {!schedule?.todays_slot && label === "no_stop_window" && (
          <div className="text-[10px] text-slate-400 italic">
            No stop window defined for today — engine runs as needed.
          </div>
        )}
        {schedule?.holiday && (
          <div className="text-amber-700">
            Holiday: <b>{schedule.holiday.name || schedule.holiday.label || "—"}</b>
            <span className="ml-1 text-[10px]">({schedule.holiday.run ? "run" : "no run"})</span>
          </div>
        )}
      </div>
    </section>
  );
}


function NextActionCard({ next_action, engine_enabled, state }) {
  const has = !!next_action;
  return (
    <section data-testid="dash-next-action-card"
             className="col-span-6 md:col-span-8 bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2">
        <FlowArrow size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Next expected action</h2>
        {!engine_enabled && (
          <span className="ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-300">
            engine off
          </span>
        )}
      </div>
      {!has && (
        <p className="text-[11px] text-slate-500 mt-2" data-testid="dash-next-empty">
          {engine_enabled
            ? "No decisions yet — waiting for the first tick."
            : "Engine is disabled. Walk the group through commissioning to start auto-sequencing."}
        </p>
      )}
      {has && (
        <div data-testid="dash-next-content" className="mt-2 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
              next_action.action === "start" ? "bg-emerald-100 text-emerald-800"
                : next_action.action === "stop" ? "bg-rose-100 text-rose-800"
                : "bg-slate-100 text-slate-700"}`}>
              {next_action.action}
            </span>
            {(next_action.target_serial_number || next_action.target_device_id) && (
              <span data-testid="dash-next-target"
                    className="font-mono text-xs text-slate-700">
                → {next_action.target_serial_number || next_action.target_device_id}
              </span>
            )}
            {next_action.countdown_s != null && (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 ml-1">
                <Clock size={11} weight="bold" /> in {Math.round(next_action.countdown_s)}s
              </span>
            )}
            {next_action.issued_command && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 ml-1">
                <CheckCircle size={11} weight="fill" /> command {next_action.command_status || "issued"}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-700 leading-relaxed">{next_action.reason}</div>
          <div className="text-[10px] text-slate-400 font-mono">
            decided {next_action.seconds_since ?? "?"}s ago · {next_action.reason_code}
          </div>
          {/* iter150AG Part 19 — operator self-help for the most common
              hold codes. Surfaces *what to do* without forcing operators
              to dig through the manual. */}
          <HoldHelp reasonCode={next_action.reason_code}
                     countdownS={next_action.countdown_s} />
        </div>
      )}
      {state !== "auto_sequencing" && (
        <p className="text-[10px] text-slate-400 mt-2">
          Decisions are advisory until the group reaches <code>auto_sequencing</code> — commands will not fire.
        </p>
      )}
    </section>
  );
}

function HoldHelp({ reasonCode, countdownS }) {
  // Map known codes → operator-facing remediation. Returns null for
  // codes we don't have explicit help text for.
  const MAP = {
    restart_delay_in_effect: {
      title: "What to do",
      body: "The engine wants to start this compressor but the configured restart delay hasn't elapsed yet. Wait for the countdown, or shorten the restart delay in Settings → Delays if your duty cycle allows.",
    },
    stop_delay_in_effect: {
      title: "What to do",
      body: "The engine wants to stop this compressor but the configured stop delay hasn't elapsed yet. Wait for the countdown, or shorten the stop delay in Settings → Delays.",
    },
    no_pressure_sample: {
      title: "What to do",
      body: "The engine has no live pressure reading to act on. Check Webhook diagnostics (CP Config → Verify) and confirm at least one member's 'Pressure' point is updating in the last minute.",
    },
    in_bandwidth: {
      title: "Why nothing is happening",
      body: "System pressure is inside the Pset±Bandwidth window — the engine intentionally holds steady. No action required.",
    },
    schedule_stop_window: {
      title: "What to do",
      body: "The current weekday's STOP window is active (Settings → Schedule). The engine will resume automatically when the window ends. Edit or delete the window if it's wrong.",
    },
    auto_disabled: {
      title: "What to do",
      body: "Auto-sequencing is paused. Enable it from the Engine tab on the Group page once you're ready to hand control to the system.",
    },
  };
  const help = MAP[reasonCode];
  if (!help) return null;
  return (
    <div data-testid={`dash-hold-help-${reasonCode}`}
         className="mt-2 bg-blue-50 border border-blue-200 rounded-sm p-2 text-[11px] leading-snug">
      <div className="flex items-center gap-2 mb-0.5">
        <div className="font-bold text-blue-800">{help.title}</div>
        <span className="ml-auto">
          <LearnMoreChip slug="05-faults-and-holds" label="Read more" />
        </span>
      </div>
      <div className="text-slate-700">{help.body}</div>
      {countdownS != null && countdownS > 0 && (
        <div className="text-[10px] text-blue-700 mt-1">
          <Clock size={10} weight="bold" className="inline -mt-0.5" /> Auto-clears in {Math.round(countdownS)}s.
        </div>
      )}
    </div>
  );
}


function MembersCard({ members, groupId, onChanged }) {
  return (
    <section data-testid="dash-members-card"
             className="col-span-12 md:col-span-8 bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Thermometer size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Members ({members.length})</h2>
        {/* iter150AG Part 19 — micro-legend for the status dots. */}
        <span className="ml-auto inline-flex items-center gap-2 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1"><StatusDot tone="blue"  animate="blink" size={7} /> running</span>
          <span className="inline-flex items-center gap-1"><StatusDot tone="red"   animate="blink" size={7} /> sleep</span>
          <span className="inline-flex items-center gap-1"><StatusDot tone="gray"  size={7} /> stopped</span>
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
            <th className="text-left py-1">Role</th>
            <th className="text-left py-1">Serial</th>
            <th className="text-left py-1">Type</th>
            <th className="text-left py-1">Mode</th>
            <th className="text-right py-1">Hours</th>
            <th className="text-right py-1">Setpoint</th>
            <th className="text-right py-1">State</th>
            <th className="text-right py-1">Control</th>
          </tr>
        </thead>
        <tbody>
          {members.map(m => {
            const dot = memberDot(m);
            return (
            <tr key={m.device_id} data-testid={`dash-member-${m.device_id}`}
                className="border-b border-slate-100 last:border-0 align-top">
              <td className="py-1.5 font-bold text-slate-700">{m.lead_lag_label}</td>
              <td className="py-1.5 font-mono text-slate-600">
                <div data-testid={`dash-member-serial-${m.device_id}`}
                     className="inline-flex items-center gap-2 truncate max-w-[200px] font-bold text-slate-900">
                  <StatusDot tone={dot.tone} animate={dot.animate} size={9} title={dot.title} />
                  {m.serial_number || <span className="italic font-normal text-slate-400">not onboarded</span>}
                </div>
                {(m.tx_topic || m.rx_topic) && (
                  <div data-testid={`dash-member-topics-${m.device_id}`}
                        className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                    {m.tx_topic && <div className="truncate"><span className="text-slate-500 font-bold">tx</span> {m.tx_topic}</div>}
                    {m.rx_topic && <div className="truncate"><span className="text-slate-500 font-bold">rx</span> {m.rx_topic}</div>}
                  </div>
                )}
              </td>
              <td className="py-1.5 text-slate-600">{m.type_code || "—"}</td>
              <td className="py-1.5">
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm ${
                  m.control_mode === "local" ? "bg-amber-100 text-amber-800"
                    : m.control_mode === "remote" ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-100 text-slate-700"}`}>
                  {m.control_mode}
                </span>
              </td>
              <td className="py-1.5 text-right font-mono text-slate-700 tabular-nums">
                {m.working_hours != null ? m.working_hours.toLocaleString() : "—"}
              </td>
              <td className="py-1.5 text-right font-mono text-slate-700 tabular-nums"
                   data-testid={`dash-member-setpoint-${m.device_id}`}>
                {m.active_setpoint_bar != null
                  ? <span>{Number(m.active_setpoint_bar).toFixed(2)} <span className="text-[9px] text-slate-400">bar</span></span>
                  : <span className="text-slate-300">—</span>}
              </td>
              <td className="py-1.5 text-right">
                {m.fault ? (
                  <span className="inline-flex items-center gap-1 text-rose-700 text-[11px] font-bold">
                    <Warning size={11} weight="fill" /> fault
                  </span>
                ) : m.engine_hold ? (
                  <span title={dot.title}
                        className="inline-flex items-center gap-1 text-rose-700 text-[11px] font-bold">
                    <Clock size={11} weight="fill" /> sleep
                  </span>
                ) : m.running ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-bold">
                    <Lightning size={11} weight="fill" /> running
                  </span>
                ) : m.running === false ? (
                  <span className="inline-flex items-center gap-1 text-slate-600 text-[11px]">
                    <Pause size={11} /> stopped
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-400 text-[11px] italic">
                    no telemetry
                  </span>
                )}
              </td>
              <td className="py-1.5 text-right">
                <RunStateToggle gid={groupId} member={m} onSent={onChanged} />
              </td>
            </tr>
          );})}
          {members.length === 0 && (
            <tr><td colSpan={8} className="py-4 text-center text-slate-400 text-xs">
              No members in this group.
            </td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}


// iter150AG Part 19 — per-member Start/Stop toggle on the Live Dashboard.
// Mirrors the same toggle on the Group Detail Members tab so operators
// can intervene from the dashboard they're already watching. Calls the
// typed CP command via our backend (`/members/{did}/start-stop`).
function RunStateToggle({ gid, member, onSent }) {
  const [busy, setBusy] = useState(false);
  const isRunning = member.running === true;
  const isStopped = member.running === false;
  async function flip() {
    if (busy) return;
    const action = isRunning ? "stop" : "start";
    const label  = member.serial_number || member.device_id;
    if (!window.confirm(`${action.toUpperCase()} ${label}? Bypasses the engine.`)) return;
    setBusy(true);
    try {
      await seqMemberStartStop(gid, member.device_id, action);
      onSent?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || `${action} failed`);
    } finally { setBusy(false); }
  }
  return (
    <button type="button" onClick={flip} disabled={busy}
            data-testid={`dash-member-runtoggle-${member.device_id}`}
            title={isRunning ? "Click to STOP" : isStopped ? "Click to START" : "Click to START"}
            className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm
                        inline-flex items-center gap-1 disabled:opacity-50 transition-colors
                        ${isRunning
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : isStopped
                            ? "bg-rose-600 text-white hover:bg-rose-700"
                            : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>
      {busy ? <CircleNotch size={10} className="animate-spin" />
            : isRunning ? <Pause size={10} weight="fill" />
                         : <Lightning size={10} weight="fill" />}
      {isRunning ? "stop" : "start"}
    </button>
  );
}


function FaultsCard({ faults }) {
  return (
    <section data-testid="dash-faults-card"
             className={`col-span-12 md:col-span-4 border rounded-sm p-4 ${
               faults.count > 0
                 ? "bg-rose-50 border-rose-200"
                 : "bg-white border-slate-200"}`}>
      <div className="flex items-center gap-2">
        <Warning size={14} className={faults.count > 0 ? "text-rose-700" : "text-slate-400"} />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Faults & warnings
        </h2>
      </div>
      {faults.count === 0 ? (
        <p className="text-[11px] text-slate-500 mt-2" data-testid="dash-faults-empty">
          All clear — no active faults.
        </p>
      ) : (
        <ul className="text-[11px] text-rose-800 mt-2 space-y-1">
          {faults.items.map(f => (
            <li key={f.device_id} data-testid={`dash-fault-${f.device_id}`}
                className="flex items-center gap-2">
              <Warning size={11} weight="fill" />
              <span className="font-mono font-bold">{f.serial_number || f.device_id}</span>
              <span className="text-slate-500">({f.lead_lag_label})</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function RecentDecisionsCard({ items }) {
  return (
    <section data-testid="dash-recent-card"
             className="col-span-12 bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <ChartLine size={14} className="text-blue-700" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Recent decisions</h2>
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-500" data-testid="dash-recent-empty">
          No decisions logged yet for this group.
        </p>
      ) : (
        <ul className="text-[11px] space-y-1">
          {items.map(d => (
            <li key={d.id} data-testid={`dash-recent-${d.id}`}
                className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-slate-400">{d.decided_at}</span>
              <span className={`text-[10px] font-bold uppercase px-1 py-0.5 rounded-sm ${
                d.action === "start" ? "bg-emerald-100 text-emerald-800"
                  : d.action === "stop" ? "bg-rose-100 text-rose-800"
                  : "bg-slate-100 text-slate-700"}`}>
                {d.action}
              </span>
              {(d.target_serial_number || d.target_device_id) && (
                <span data-testid={`dash-recent-target-${d.id}`}
                      className="font-mono text-slate-700">
                  {d.target_serial_number || d.target_device_id}
                </span>
              )}
              <span className="text-slate-500 truncate">{d.reason}</span>
              {d.issued_command && (
                <span className="text-[10px] text-emerald-700 ml-auto">cmd ✓</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}



// iter150AG Part 9 (P2) — Fault alert banner.
// Shows the count of OPEN alerts at the top of the dashboard. Each row
// links to the member device and exposes the open timestamp so operators
// know how long a fault has been unaddressed.
function AlertBanner({ alerts }) {
  const open = alerts?.open || [];
  if (open.length === 0) return null;
  return (
    <section data-testid="dash-alert-banner"
              className="bg-rose-50 border border-rose-300 rounded-sm px-4 py-3 flex items-start gap-3">
      <Warning size={20} weight="fill" className="text-rose-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold font-['Chivo'] text-rose-900">
          {open.length === 1
            ? "1 active fault alert"
            : `${open.length} active fault alerts`}
          <span className="ml-2 text-[11px] font-normal text-rose-700">
            — operators + admins have been notified by email
          </span>
        </div>
        <ul className="mt-1.5 space-y-0.5">
          {open.map((a) => (
            <li key={a.id}
                data-testid={`dash-alert-${a.device_id}`}
                className="text-[11px] text-rose-800 flex items-center gap-2">
              <span className="font-mono font-bold">{a.serial_number || a.device_id}</span>
              <span className="text-rose-600">·</span>
              <span>{a.kind === "member_fault" ? "member fault" : a.kind}</span>
              <span className="text-rose-600">·</span>
              <span className="text-rose-600">
                opened <RelativeTime iso={a.opened_at} />
              </span>
              {Array.isArray(a.notified_emails) && a.notified_emails.length > 0 && (
                <span className="text-[10px] text-rose-500 ml-auto">
                  notified {a.notified_emails.length}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}


function RelativeTime({ iso }) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  const ago = Math.max(0, (Date.now() - ts) / 1000);
  if (ago < 60)   return <>{Math.floor(ago)}s ago</>;
  if (ago < 3600) return <>{Math.floor(ago / 60)} min ago</>;
  if (ago < 86400) return <>{Math.floor(ago / 3600)} h ago</>;
  return <>{Math.floor(ago / 86400)} d ago</>;
}
